import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronRight,
  Folder,
  FileText,
  HardDrive,
  ArrowLeft,
  Home,
  Copy,
  Check,
} from "lucide-react";
import { api } from "../lib/api";
import { formatBytes, formatDate, formatDateTime, formatNumber } from "../lib/format";
import { cn } from "../lib/cn";

export default function DiskDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [path, setPath] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const diskQ = useQuery({
    queryKey: ["disk", id],
    queryFn: () => api.getDisk(id!),
    enabled: !!id,
  });

  const treeQ = useQuery({
    queryKey: ["disk-tree", id, path],
    queryFn: () => api.diskTree(id!, path),
    enabled: !!id,
  });

  if (!id) return <div>Disque introuvable</div>;
  const disk = diskQ.data;
  const nodes = treeQ.data ?? [];

  const segments = path ? path.split("/") : [];

  const onCopy = async (text: string): Promise<void> => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <div className="space-y-8">
      <Link to="/disques" className="inline-flex items-center gap-2 text-caption text-foreground/60 hover:text-foreground transition-colors">
        <ArrowLeft className="h-3.5 w-3.5" />
        Retour aux disques
      </Link>

      {disk && (
        <>
          <section>
            <div className="label-caps mb-2">Disque</div>
            <h1 className="title-dual text-h1">
              {disk.label}{" "}
              {disk.volumeName && <span className="dim">· {disk.volumeName}</span>}
            </h1>
            {disk.notes && <p className="mt-3 text-foreground/60 text-lead max-w-2xl">{disk.notes}</p>}
          </section>

          <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Fichiers" value={formatNumber(disk.fileCount)} />
            <Stat label="Taille indexée" value={formatBytes(disk.usedSize)} />
            <Stat label="Capacité totale" value={formatBytes(disk.totalSize)} />
            <Stat label="Dernier scan" value={formatDateTime(disk.scannedAt)} />
          </section>
        </>
      )}

      <section className="card-koloc">
        <div className="flex items-center gap-2 flex-wrap text-caption">
          <button
            onClick={() => setPath(null)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-pill px-3 py-1 transition-all",
              path === null
                ? "bg-foreground text-background"
                : "text-foreground/60 hover:text-foreground hover:bg-foreground/5"
            )}
          >
            <Home className="h-3.5 w-3.5" />
            Racine
          </button>
          {segments.map((seg, i) => {
            const subPath = segments.slice(0, i + 1).join("/");
            return (
              <span key={subPath} className="inline-flex items-center gap-1">
                <ChevronRight className="h-3.5 w-3.5 text-foreground/30" />
                <button
                  onClick={() => setPath(subPath)}
                  className={cn(
                    "rounded-pill px-3 py-1 transition-all",
                    i === segments.length - 1
                      ? "bg-foreground text-background"
                      : "text-foreground/60 hover:text-foreground hover:bg-foreground/5"
                  )}
                >
                  {seg}
                </button>
              </span>
            );
          })}
          {path && (
            <button
              onClick={() => onCopy(path)}
              className="ml-2 inline-flex items-center gap-1.5 rounded-pill px-3 py-1 text-foreground/50 hover:text-foreground hover:bg-foreground/5 transition-all"
            >
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              Copier
            </button>
          )}
        </div>

        <div className="mt-6 divider" />

        {treeQ.isLoading ? (
          <div className="py-12 text-center text-caption text-foreground/40">Chargement…</div>
        ) : nodes.length === 0 ? (
          <div className="py-12 text-center text-caption text-foreground/40">
            <HardDrive className="mx-auto mb-3 h-6 w-6 text-foreground/20" />
            Dossier vide.
          </div>
        ) : (
          <ul className="divide-y divide-foreground/8">
            {nodes.map((n) => (
              <li
                key={n.path}
                className="py-3 flex items-center gap-4 hover:bg-foreground/[0.02] -mx-6 px-6 transition-colors"
              >
                <div className="shrink-0 text-foreground/40">
                  {n.isDirectory ? <Folder className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  {n.isDirectory ? (
                    <button
                      onClick={() => setPath(n.path)}
                      className="text-body font-medium text-left truncate hover:underline"
                    >
                      {n.name}
                    </button>
                  ) : (
                    <span className="text-body font-medium truncate block">{n.name}</span>
                  )}
                </div>
                <div className="shrink-0 text-caption text-foreground/50 num text-right tabular-nums w-24">
                  {formatBytes(n.sizeBytes)}
                </div>
                <div className="shrink-0 text-caption text-foreground/40 num w-28 text-right">
                  {formatDate(n.modifiedAtFs)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="card-koloc">
      <div className="label-caps">{label}</div>
      <div className="mt-2 text-h3 num">{value}</div>
    </div>
  );
}
